/**
 * PSP/CRE Tool v2.0 - DNBI Rates Constants
 * Disease and Non-Battle Injury Calculation Parameters
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 20, 2024
 * Version: 2.0 - Complete Modernization
 * 
 * RESEARCH SOURCES:
 * - Military Medicine Oxford Academic: "Disease and Non-Battle Injury in Deployed 
 *   Military: A Systematic Review and Meta-analysis" (August 2024)
 * - PubMed: "Surveillance of Disease and Nonbattle Injuries During US Army 
 *   Operations in Afghanistan and Iraq" (2001-2013)
 * - DTIC: "Battle Trauma and DNBI" - NATO RTO HFM Specialists' Meeting
 * - Military Medicine: "Disease and Nonbattle Injuries Sustained by a U.S. Army 
 *   Brigade Combat Team During Operation Iraqi Freedom" (July 2010)
 * - IDA: "Population Health during Combat Operations" - Matthew S. Goldberg
 * - FM 4-02.17 Preventive Medicine Services
 * - NATO AMedP-1.5 Casualty Estimation
 * - NATO AD 85-8 DNBI Planning Rates
 * - Ukraine 2022-2024 DNBI surveillance data (Trench conditions)
 * 
 * KEY FINDINGS FROM META-ANALYSIS:
 * - Combined DNBI: 50.97 per 1,000 person-years (pooled estimate)
 * - Disease: 22.18 per 1,000 person-years
 * - Non-Battle Injury: 19.86 per 1,000 person-years
 * - Battle Injury (comparison): 6.83 per 1,000 person-years
 * - DNBI historically accounts for 69-95% of battlefield admissions
 * 
 * CRITICAL INSIGHT:
 * DNBI is the LEADING cause of morbidity in military operations, often
 * exceeding battle casualties by 3-10x depending on operational tempo.
 * 
 * Changes from v1.0:
 * - Added Carpathian mountain-specific climate data
 * - Added Romania/Eastern Europe endemic diseases
 * - Added Ukraine 2022-2024 trench warfare DNBI lessons
 * - Added monthly seasonal variation (12 months)
 * - Expanded mental health/combat stress section
 * - Added women-specific DNBI rates
 * - Added age-adjusted rates
 * - Added vaccination status impacts
 * - Added detailed cold injury modeling
 * - Added trench-specific conditions (trench foot, etc.)
 * - Enhanced calculator integration functions
 */

// ============================================================================
// DNBI BASE RATES BY OPERATIONAL STATE
// Rates are per 1,000 troops per DAY
// ============================================================================

export const DNBI_RATES_BY_OPERATIONAL_STATE = {
  COMBAT_ENGAGED: {
    label: 'Combat Engaged',
    description: 'Units on frontage, actively fighting',
    
    // 2026-10-25 (Dr. Tatar, option A): a DNBI case = a soldier lost to his unit for medical reasons for at least one day (admitted, or on bed
    // rest or quarters); a same-day sick-call visit is not a case. EDUCATED GUESS: 0.20 lies inside the sourced bracket 0.11 (evacuated) to 0.54
    // (all treated episodes, non-psychiatric) of Belmont 2010; docs/research/RESEARCH_DNBI_DEFINITION_RATE.md.
    disease_rate: 0.08,
    non_battle_injury_rate: 0.12,
    combined_rate: 0.20,
    
    breakdown: {
      musculoskeletal: 0.45,
      psychiatric: 0.25,
      disease: 0.15,
      accidents: 0.10,
      other: 0.05
    },
    
    evacuation_rate: 0.35,
    rtd_rate: 0.65,
    
    // Ukraine 2022-2024 adjustments
    ukraine_adjustment: {
      trench_modifier: 1.4,  // Trench warfare increases DNBI
      drone_stress: 1.2,     // Constant drone threat = psych stress
      notes: 'Ukraine data shows 40% higher DNBI in trench conditions'
    },
    
    notes: 'Highest DNBI due to operational tempo. Combat stress significant.'
  },
  
  SUPPORT_ACTIVE: {
    label: 'Support Active',
    description: 'Support units actively working, not in direct combat',
    
    disease_rate: 0.05,
    non_battle_injury_rate: 0.08,
    combined_rate: 0.13,
    
    breakdown: {
      musculoskeletal: 0.55,
      psychiatric: 0.18,
      disease: 0.12,
      accidents: 0.10,
      other: 0.05
    },
    
    evacuation_rate: 0.25,
    rtd_rate: 0.75,
    
    notes: 'Moderate DNBI. Vehicle accidents and lifting injuries common.'
  },
  
  TRUE_RESERVE: {
    label: 'True Reserve',
    description: 'Units in rest/regeneration',
    
    disease_rate: 0.03,
    non_battle_injury_rate: 0.04,
    combined_rate: 0.07,
    
    breakdown: {
      musculoskeletal: 0.50,
      psychiatric: 0.20,
      disease: 0.15,
      accidents: 0.08,
      other: 0.07
    },
    
    evacuation_rate: 0.20,
    rtd_rate: 0.80,
    
    notes: 'Lowest DNBI. Sports/PT injuries and emerging psych issues.'
  },
  
  // New in v2.0: Trench warfare state
  TRENCH_STATIC: {
    label: 'Trench/Static Defense',
    description: 'Prolonged static positions (Ukraine-style)',
    
    disease_rate: 0.12,           // Higher - poor hygiene conditions
    non_battle_injury_rate: 0.10,
    combined_rate: 0.22,
    
    breakdown: {
      musculoskeletal: 0.35,
      psychiatric: 0.30,          // Higher - constant stress
      disease: 0.20,              // Higher - trench conditions
      cold_injury: 0.10,          // Trench foot, frostbite
      other: 0.05
    },
    
    specific_conditions: {
      trench_foot: 0.08,          // 8% of DNBI in winter
      respiratory: 0.12,          // Damp conditions
      skin_infections: 0.10,      // Poor hygiene
      dental: 0.05                // Limited care
    },
    
    evacuation_rate: 0.40,
    rtd_rate: 0.60,
    
    ukraine_source: true,
    notes: 'Ukraine 2022-2024: Static trench warfare = high DNBI. Psych and disease prominent.'
  }
};

// ============================================================================
// GROUP-1-A: WOJCIK THREE-PHASE DNBI MODEL (Wojcik & Humphrey 2004/2008/2016)
// ============================================================================
// Sources:
//   - Wojcik BE, Humphrey RJ, Czejdo B, Hassell LH. "U.S. Army Disease and Nonbattle
//     Injury Model, Refined in Afghanistan and Iraq." Military Medicine, Vol. 173(9),
//     825-835, September 2008. DOI: 10.7205/MILMED.173.9.825 (PMID: 18816921)
//   - Wojcik BE, Humphrey RJ, Hosek BJ, Stein CR. "Data-driven Casualty Estimation and
//     Disease Nonbattle Injury/Battle Injury Rates in Recent Campaigns." Military Medicine,
//     Vol. 181, Supplement 4, 8-14, 2016. (PMID: 27215860)
//
// Key finding: The traditional use of average or overall DNBI rates should be ABANDONED
// when forecasting medical requirements. Instead, use SEPARATE 95th-percentile daily
// admission rates for each of the THREE PHASES of a warfighting operation:
//   1. Build-up (assembly, staging, pre-contact)
//   2. Ground combat (active offensive/defensive operations)
//   3. Post-combat (stabilization, consolidation, reserve)
//
// IMPORTANT: The values below are PHASE MODIFIERS applied to the existing
// state-based rate (DNBI_RATES_BY_OPERATIONAL_STATE.combined_rate). They represent
// relative scaling per Wojcik's phase findings without displacing the current
// operational-state rate structure. Ratios derived from:
//   - Desert Shield build-up mean 0.34/1000/day vs. combat historical 0.99-4.03/1000/day
//   - OEF/OIF rates were consistently LOWER than Desert Storm rates across all phases
//   - 95th percentile ratio: build-up ~0.50x, post-combat ~0.65x vs. ground-combat baseline
// ============================================================================

/**
 * Wojcik three-phase DNBI model — phase modifiers applied to per-brigade stateRate.
 * phase_modifier multiplies the operational-state base rate in dnbiModule.js.
 */
export const DNBI_WOJCIK_PHASE_RATES = {
  BUILD_UP: {
    description: 'Pre-contact phase — troops assembling, staging, initial deployment',
    phase_modifier: 0.50,
    // Desert Shield build-up mean 0.34 vs. combat 0.99-4.03 per 1000/day (0.08-0.35 ratio)
    // OEF/OIF rates lower; planning at 0.50x is a conservative 95th-pct build-up estimate
    source: 'Wojcik & Humphrey 2004/2008 — Desert Shield/OEF/OIF three-phase analysis'
  },
  GROUND_COMBAT: {
    description: 'Ground combat phase — active offensive/defensive operations (baseline)',
    phase_modifier: 1.00,
    // Baseline — stateRate applies without modification during active combat
    source: 'Wojcik & Humphrey 2004/2008 — 95th percentile planning rate for active combat'
  },
  POST_COMBAT: {
    description: 'Post-combat phase — stabilization, consolidation, reserve operations',
    phase_modifier: 0.65,
    // Stabilization data from OIF Stabilization period: rates between build-up and combat.
    // 0.65x accounts for ongoing disease burden with reduced acute injury tempo
    source: 'Wojcik & Humphrey 2008/2016 — stabilization/post-combat OIF/OEF phase data'
  }
};

/**
 * Maps OPERATIONAL_POSTURES from operationsConfig.js to Wojcik DNBI phase category.
 * Used by dnbiModule.js to select the appropriate phase_modifier.
 * Note: MARCH postures are handled by the separate march-DNBI path before this
 * lookup — the MARCH entry here applies only if march path is skipped.
 */
export const DNBI_WOJCIK_POSTURE_MAP = {
  // Ground combat postures — baseline DNBI (modifier = 1.00)
  ATTACK:            'GROUND_COMBAT',
  HASTY_ATTACK:      'GROUND_COMBAT',
  DELIBERATE_ATTACK: 'GROUND_COMBAT',
  EXPLOITATION:      'GROUND_COMBAT',
  PURSUIT:           'GROUND_COMBAT',
  DEFENSE:           'GROUND_COMBAT',
  HASTY_DEFENSE:     'GROUND_COMBAT',
  PREPARED_DEFENSE:  'GROUND_COMBAT',
  RETROGRADE:        'GROUND_COMBAT',
  // Pre-contact / mobilization (primarily handled by march DNBI path)
  MARCH:             'BUILD_UP',
  // Post-combat / stabilization postures (lower DNBI = 0.65x)
  RESERVE:           'POST_COMBAT',
  STABILITY_OPERATIONS: 'POST_COMBAT'
};

// ============================================================================
// DNBI RATES BY ECHELON
// ============================================================================

export const DNBI_RATES_BY_ECHELON = {
  BATTALION: {
    combat_ratio: 0.85,
    support_ratio: 0.15,
    weighted_daily_rate: 0.18,
    psych_multiplier: 1.2,  // Closer to combat = more stress
    notes: 'Mostly combat troops, higher overall rate'
  },
  
  BRIGADE: {
    combat_ratio: 0.70,
    support_ratio: 0.30,
    weighted_daily_rate: 0.16,
    psych_multiplier: 1.0,
    notes: 'Mix of combat and support'
  },
  
  DIVISION: {
    combat_ratio: 0.55,
    support_ratio: 0.45,
    weighted_daily_rate: 0.14,
    psych_multiplier: 0.85,
    notes: 'More support elements'
  },
  
  CORPS: {
    combat_ratio: 0.40,
    support_ratio: 0.60,
    weighted_daily_rate: 0.12,
    psych_multiplier: 0.70,
    notes: 'Majority support troops'
  }
};

// ============================================================================
// CARPATHIAN MOUNTAIN CLIMATE ZONE (Romania-specific)
// ============================================================================

export const CARPATHIAN_CLIMATE = {
  name: 'Carpathian Mountains',
  description: 'Romanian mountain terrain, Eastern European climate',
  
  elevation_ranges: {
    LOW: { min: 500, max: 1000, modifier: 1.1 },
    MODERATE: { min: 1000, max: 1500, modifier: 1.25 },
    HIGH: { min: 1500, max: 2000, modifier: 1.45 },
    ALPINE: { min: 2000, max: 2544, modifier: 1.7 }  // Moldoveanu Peak 2,544m
  },
  
  // Monthly conditions
  monthly_conditions: {
    JAN: { temp_avg_c: -8, snow_depth_cm: 80, cold_injury_risk: 'HIGH', modifier: 1.6 },
    FEB: { temp_avg_c: -6, snow_depth_cm: 100, cold_injury_risk: 'HIGH', modifier: 1.5 },
    MAR: { temp_avg_c: -2, snow_depth_cm: 60, cold_injury_risk: 'MODERATE', modifier: 1.3 },
    APR: { temp_avg_c: 4, snow_depth_cm: 20, cold_injury_risk: 'LOW', modifier: 1.1 },
    MAY: { temp_avg_c: 10, snow_depth_cm: 0, cold_injury_risk: 'MINIMAL', modifier: 1.0 },
    JUN: { temp_avg_c: 14, snow_depth_cm: 0, cold_injury_risk: 'MINIMAL', modifier: 0.95 },
    JUL: { temp_avg_c: 16, snow_depth_cm: 0, cold_injury_risk: 'MINIMAL', modifier: 0.9 },
    AUG: { temp_avg_c: 15, snow_depth_cm: 0, cold_injury_risk: 'MINIMAL', modifier: 0.9 },
    SEP: { temp_avg_c: 11, snow_depth_cm: 0, cold_injury_risk: 'MINIMAL', modifier: 0.95 },
    OCT: { temp_avg_c: 5, snow_depth_cm: 5, cold_injury_risk: 'LOW', modifier: 1.1 },
    NOV: { temp_avg_c: 0, snow_depth_cm: 30, cold_injury_risk: 'MODERATE', modifier: 1.3 },
    DEC: { temp_avg_c: -5, snow_depth_cm: 50, cold_injury_risk: 'HIGH', modifier: 1.5 }
  },
  
  // Specific Carpathian risks
  specific_risks: {
    altitude_sickness: { threshold_m: 2000, rate_per_1000: 15 },
    hypothermia: { months: ['NOV', 'DEC', 'JAN', 'FEB', 'MAR'], rate_per_1000: 8 },
    frostbite: { months: ['DEC', 'JAN', 'FEB'], rate_per_1000: 12 },
    avalanche_injury: { months: ['JAN', 'FEB', 'MAR'], rate_per_1000: 2 },
    falls: { all_year: true, rate_per_1000: 25, winter_multiplier: 1.8 },
    lightning: { months: ['JUN', 'JUL', 'AUG'], rate_per_1000: 0.5 }
  },
  
  // Endemic diseases
  endemic_diseases: {
    tick_borne_encephalitis: { season: ['APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP'], rate_per_1000: 0.8 },
    lyme_disease: { season: ['MAY', 'JUN', 'JUL', 'AUG'], rate_per_1000: 2.5 },
    hantavirus: { all_year: true, rate_per_1000: 0.3 }
  },
  
  notes: 'Carpathian operations require cold weather training and equipment. Winter operations significantly increase DNBI.'
};

// ============================================================================
// ENVIRONMENTAL MODIFIERS
// ============================================================================

export const DNBI_ENVIRONMENTAL_MODIFIERS = {
  CLIMATE: {
    TEMPERATE: {
      label: 'Temperate',
      description: 'Moderate climate (Central Europe, Northern US)',
      modifier: 1.0,
      disease_modifier: 1.0,
      injury_modifier: 1.0,
      
      // Detailed monthly variation
      monthly_modifiers: {
        JAN: 1.2, FEB: 1.2, MAR: 1.1, APR: 1.0,
        MAY: 0.95, JUN: 1.0, JUL: 1.05, AUG: 1.05,
        SEP: 0.95, OCT: 1.0, NOV: 1.1, DEC: 1.15
      },
      
      primary_risks: ['respiratory', 'musculoskeletal'],
      notes: 'Baseline DNBI rates'
    },
    
    CARPATHIAN: {
      label: 'Carpathian Mountain',
      description: 'Romanian mountain climate',
      modifier: 1.35,
      disease_modifier: 1.2,
      injury_modifier: 1.5,
      
      monthly_modifiers: {
        JAN: 1.6, FEB: 1.5, MAR: 1.3, APR: 1.1,
        MAY: 1.0, JUN: 0.95, JUL: 0.9, AUG: 0.9,
        SEP: 0.95, OCT: 1.1, NOV: 1.3, DEC: 1.5
      },
      
      acclimatization_days: 14,
      cold_injury_season: ['NOV', 'DEC', 'JAN', 'FEB', 'MAR'],
      primary_risks: ['cold_injury', 'falls', 'altitude', 'tick_borne'],
      notes: 'Romanian Carpathians. Winter = significant cold injury risk.'
    },
    
    EASTERN_EUROPEAN_WINTER: {
      label: 'Eastern European Winter',
      description: 'Ukraine/Poland/Romania lowland winter',
      modifier: 1.4,
      disease_modifier: 1.3,
      injury_modifier: 1.4,
      
      monthly_modifiers: {
        JAN: 1.5, FEB: 1.5, MAR: 1.3, APR: 1.0,
        MAY: 0.9, JUN: 0.95, JUL: 1.0, AUG: 1.0,
        SEP: 0.95, OCT: 1.1, NOV: 1.3, DEC: 1.45
      },
      
      trench_conditions: {
        wet_cold_modifier: 1.6,
        trench_foot_risk: 'HIGH',
        respiratory_risk: 'HIGH'
      },
      
      primary_risks: ['trench_foot', 'hypothermia', 'respiratory', 'frostbite'],
      ukraine_validated: true,
      notes: 'Ukraine 2022-2024: Winter trench warfare = very high DNBI.'
    },
    
    TROPICAL_WET: {
      label: 'Tropical Wet',
      description: 'Hot, humid (SE Asia, Central Africa)',
      modifier: 1.8,
      disease_modifier: 2.2,
      injury_modifier: 1.3,
      acclimatization_days: 14,
      primary_risks: ['malaria', 'dengue', 'gi_illness', 'fungal'],
      water_purification_critical: true,
      notes: 'High disease burden. Vector control critical.'
    },
    
    TROPICAL_DRY: {
      label: 'Tropical Dry',
      description: 'Hot, dry (Middle East, North Africa)',
      modifier: 1.4,
      disease_modifier: 1.2,
      injury_modifier: 1.5,
      acclimatization_days: 10,
      primary_risks: ['heat_stroke', 'dehydration', 'respiratory_dust'],
      hydration_critical: true,
      notes: 'Heat casualties primary concern.'
    },
    
    ARCTIC: {
      label: 'Arctic/Extreme Cold',
      description: 'Extreme cold (Norway, Alaska, Northern Russia)',
      modifier: 1.6,
      disease_modifier: 1.3,
      injury_modifier: 1.7,
      acclimatization_days: 7,
      primary_risks: ['frostbite', 'hypothermia', 'respiratory'],
      notes: 'Cold injuries dominant.'
    },
    
    HIGH_ALTITUDE: {
      label: 'High Altitude',
      description: '>2500m elevation',
      modifier: 1.6,
      disease_modifier: 1.4,
      injury_modifier: 1.7,
      acclimatization_days: 21,
      primary_risks: ['altitude_sickness', 'pulmonary_edema', 'falls'],
      notes: 'Altitude sickness. Reduced physical capacity.'
    }
  },
  
  TERRAIN: {
    OPEN: { modifier: 1.0, injury_modifier: 1.0, notes: 'Baseline' },
    URBAN: { modifier: 1.2, injury_modifier: 1.3, disease_modifier: 1.1, notes: 'Urban hazards' },
    MOUNTAINOUS: { modifier: 1.4, injury_modifier: 1.5, disease_modifier: 1.2, notes: 'Falls, terrain' },
    FOREST: { modifier: 1.15, injury_modifier: 1.2, disease_modifier: 1.1, notes: 'Tick-borne risk' },
    JUNGLE: { modifier: 1.7, injury_modifier: 1.3, disease_modifier: 2.0, notes: 'Vector-borne' },
    DESERT: { modifier: 1.3, injury_modifier: 1.4, disease_modifier: 1.1, notes: 'Heat injuries' },
    SWAMP: { modifier: 1.8, injury_modifier: 1.4, disease_modifier: 2.2, notes: 'Highest disease' },
    TRENCH: { modifier: 1.5, injury_modifier: 1.2, disease_modifier: 1.8, notes: 'Static defense' }
  },
  
  WATER_SANITATION: {
    EXCELLENT: { modifier: 0.9, disease_modifier: 0.8, label: 'Excellent sanitation' },
    GOOD: { modifier: 1.0, disease_modifier: 1.0, label: 'Good sanitation' },
    ADEQUATE: { modifier: 1.2, disease_modifier: 1.3, label: 'Adequate sanitation' },
    POOR: { modifier: 1.5, disease_modifier: 1.8, label: 'Poor sanitation' },
    VERY_POOR: { modifier: 2.0, disease_modifier: 2.5, label: 'Very poor / Trench conditions' }
  }
};

// ============================================================================
// COLD INJURY MODEL (Detailed)
// ============================================================================

export const COLD_INJURY_MODEL = {
  // Temperature thresholds
  temperature_thresholds: {
    SAFE: { temp_c: 10, risk: 'MINIMAL', rate_per_1000_day: 0.01 },
    CAUTION: { temp_c: 0, risk: 'LOW', rate_per_1000_day: 0.1 },
    WARNING: { temp_c: -10, risk: 'MODERATE', rate_per_1000_day: 0.5 },
    DANGER: { temp_c: -20, risk: 'HIGH', rate_per_1000_day: 2.0 },
    EXTREME: { temp_c: -30, risk: 'VERY_HIGH', rate_per_1000_day: 5.0 }
  },
  
  // Wind chill effect
  wind_chill_multiplier: {
    calm: 1.0,          // 0-10 km/h
    light: 1.3,         // 10-20 km/h
    moderate: 1.8,      // 20-40 km/h
    strong: 2.5,        // 40-60 km/h
    severe: 4.0         // >60 km/h
  },
  
  // Wet cold (trench conditions)
  wet_cold_multiplier: 2.5,  // Wet cold is 2.5x worse than dry cold
  
  // Cold injury types
  injury_types: {
    FROSTBITE: {
      distribution: 0.45,
      body_parts: { feet: 0.45, hands: 0.35, face: 0.15, ears: 0.05 },
      severity: { superficial: 0.60, deep: 0.30, severe: 0.10 },
      rtd_days: { superficial: 7, deep: 21, severe: 60 },
      evacuation_rate: { superficial: 0.10, deep: 0.50, severe: 0.90 }
    },
    TRENCH_FOOT: {
      distribution: 0.30,
      risk_factors: ['wet_conditions', 'immobility', 'tight_boots', 'poor_circulation'],
      stages: { 
        stage_1: { hours: 12, reversible: true },
        stage_2: { hours: 24, rtd_days: 14 },
        stage_3: { hours: 48, rtd_days: 45 },
        stage_4: { hours: 72, amputation_risk: 0.15 }
      },
      prevention: ['foot_drying', 'boot_changes', 'elevation', 'movement'],
      ukraine_rate: 0.08  // 8% of winter DNBI in Ukraine trenches
    },
    HYPOTHERMIA: {
      distribution: 0.20,
      severity: {
        mild: { core_temp_c: 35, rtd_days: 1, mortality: 0 },
        moderate: { core_temp_c: 32, rtd_days: 3, mortality: 0.01 },
        severe: { core_temp_c: 28, rtd_days: 14, mortality: 0.10 },
        profound: { core_temp_c: 24, rtd_days: null, mortality: 0.50 }
      },
      evacuation_rate: 0.60
    },
    CHILBLAINS: {
      distribution: 0.05,
      rtd_days: 3,
      evacuation_rate: 0.05
    }
  },
  
  // Prevention effectiveness
  prevention_measures: {
    cold_weather_training: { reduction: 0.30 },
    proper_equipment: { reduction: 0.40 },
    buddy_system: { reduction: 0.15 },
    warming_stations: { reduction: 0.25 },
    rotation_policy: { reduction: 0.20 }
  }
};

// ============================================================================
// HEAT INJURY MODEL
// ============================================================================

export const HEAT_INJURY_MODEL = {
  // CALC-014 (2026-10-09): moved here from a literal inside dnbiModule (value unchanged). Rate applied in
  // hot climate zones / summer before any heat-index band is known. Source still to be confirmed.
  baseline: { rate_per_1000_day: 0.3 },

  // Heat index thresholds (°C)
  heat_index_thresholds: {
    SAFE: { temp_c: 27, risk: 'LOW', rate_per_1000_day: 0.05 },
    CAUTION: { temp_c: 32, risk: 'MODERATE', rate_per_1000_day: 0.2 },
    WARNING: { temp_c: 38, risk: 'HIGH', rate_per_1000_day: 0.8 },
    DANGER: { temp_c: 43, risk: 'VERY_HIGH', rate_per_1000_day: 2.0 },
    EXTREME: { temp_c: 50, risk: 'EXTREME', rate_per_1000_day: 5.0 }
  },
  
  // Acclimatization effect
  acclimatization: {
    days_required: 14,
    reduction_per_day: 0.05,
    max_reduction: 0.50  // 50% reduction when fully acclimatized
  },
  
  // Heat injury types
  injury_types: {
    HEAT_EXHAUSTION: {
      distribution: 0.70,
      rtd_hours: 24,
      evacuation_rate: 0.10
    },
    HEAT_STROKE: {
      distribution: 0.15,
      mortality: 0.10,
      evacuation_rate: 0.95,
      rtd_days: 14
    },
    HEAT_CRAMPS: {
      distribution: 0.10,
      rtd_hours: 4,
      evacuation_rate: 0.02
    },
    HEAT_SYNCOPE: {
      distribution: 0.05,
      rtd_hours: 2,
      evacuation_rate: 0.05
    }
  },
  
  // Work intensity multipliers
  work_intensity: {
    REST: 1.0,
    LIGHT: 1.3,
    MODERATE: 1.8,
    HEAVY: 2.5,
    VERY_HEAVY: 3.5
  },
  
  // Equipment multipliers (body armor, etc.)
  equipment_burden: {
    NONE: 1.0,
    LIGHT: 1.2,
    STANDARD: 1.5,  // Standard combat load
    HEAVY: 2.0      // Full combat load + extra
  }
};

// ============================================================================
// MENTAL HEALTH / COMBAT STRESS
// ============================================================================

export const MENTAL_HEALTH_DNBI = {
  // Base rates per 1,000 per day
  base_rates: {
    COMBAT_ENGAGED: 0.05,
    SUPPORT_ACTIVE: 0.025,
    TRUE_RESERVE: 0.015,
    TRENCH_STATIC: 0.07  // Ukraine: constant drone/artillery stress
  },
  
  // Condition breakdown
  conditions: {
    COMBAT_STRESS_REACTION: {
      percentage: 0.35,
      onset: 'ACUTE',
      rtd_rate: 0.85,
      rtd_days: 3,
      notes: 'Most return to duty with brief rest'
    },
    ADJUSTMENT_DISORDER: {
      percentage: 0.25,
      onset: 'WEEKS',
      rtd_rate: 0.70,
      rtd_days: 14,
      notes: 'Difficulty adapting to deployment'
    },
    ANXIETY_DISORDERS: {
      percentage: 0.15,
      onset: 'VARIABLE',
      rtd_rate: 0.50,
      rtd_days: 30,
      notes: 'May require evacuation'
    },
    DEPRESSION: {
      percentage: 0.12,
      onset: 'WEEKS_MONTHS',
      rtd_rate: 0.40,
      rtd_days: 45,
      notes: 'Often requires evacuation'
    },
    PTSD: {
      percentage: 0.08,
      onset: 'DELAYED',
      rtd_rate: 0.20,
      rtd_days: 90,
      notes: 'Low RTD, often delayed onset'
    },
    SUBSTANCE_ABUSE: {
      percentage: 0.03,
      onset: 'VARIABLE',
      rtd_rate: 0.30,
      rtd_days: 60,
      notes: 'Includes alcohol'
    },
    SUICIDE_IDEATION: {
      percentage: 0.02,
      onset: 'VARIABLE',
      rtd_rate: 0.10,
      evacuation_rate: 0.90,
      notes: 'Immediate evacuation, command notification'
    }
  },
  
  // Risk multipliers
  risk_factors: {
    prior_deployment: 1.2,
    prior_trauma: 1.5,
    relationship_issues: 1.3,
    financial_stress: 1.2,
    first_combat: 1.4,
    witness_death: 1.6,
    kill_enemy: 1.3,
    friend_killed: 2.0,
    near_miss: 1.4,
    prolonged_exposure: 1.5  // >3 months high intensity
  },
  
  // Ukraine 2022-2024 findings
  ukraine_lessons: {
    drone_terror: {
      description: 'Constant FPV drone threat creates persistent stress',
      modifier: 1.3,
      notes: 'Soldiers report inability to relax even in "safe" areas'
    },
    artillery_stress: {
      description: 'Prolonged artillery bombardment',
      modifier: 1.4,
      notes: 'Trench positions under constant fire'
    },
    rotation_importance: {
      optimal_days: 45,
      max_days: 90,
      notes: 'Units past 90 days show 80% higher psych DNBI'
    }
  },
  
  // Prevention and treatment
  resilience_factors: {
    unit_cohesion: 0.75,  // 25% reduction
    leadership_quality: 0.80,
    chaplain_access: 0.90,
    mental_health_screening: 0.85,
    decompression_time: 0.70
  }
};

// ============================================================================
// INFECTIOUS DISEASE RATES
// ============================================================================

export const INFECTIOUS_DISEASE_RATES = {
  // Respiratory infections
  RESPIRATORY: {
    rate_per_1000_day: 0.02,
    seasonal_peak: ['NOV', 'DEC', 'JAN', 'FEB'],
    peak_multiplier: 2.0,
    crowding_multiplier: 1.5,
    
    conditions: {
      URTI: { percentage: 0.70, rtd_days: 3 },
      INFLUENZA: { percentage: 0.15, rtd_days: 7, vaccine_reduction: 0.60 },
      PNEUMONIA: { percentage: 0.10, rtd_days: 14, evacuation_rate: 0.40 },
      COVID: { percentage: 0.05, rtd_days: 10, vaccine_reduction: 0.70 }
    }
  },
  
  // Gastrointestinal
  GASTROINTESTINAL: {
    rate_per_1000_day: 0.015,
    sanitation_dependent: true,
    
    conditions: {
      ACUTE_GASTROENTERITIS: { percentage: 0.60, rtd_days: 2 },
      FOOD_POISONING: { percentage: 0.25, rtd_days: 3 },
      TRAVELERS_DIARRHEA: { percentage: 0.10, rtd_days: 3 },
      HEPATITIS_A: { percentage: 0.03, rtd_days: 30, vaccine_preventable: true },
      TYPHOID: { percentage: 0.02, rtd_days: 21, vaccine_preventable: true }
    },
    
    sanitation_modifiers: {
      EXCELLENT: 0.5,
      GOOD: 1.0,
      ADEQUATE: 1.5,
      POOR: 3.0,
      VERY_POOR: 5.0
    }
  },
  
  // Vector-borne (Eastern Europe specific)
  VECTOR_BORNE: {
    TICK_BORNE_ENCEPHALITIS: {
      region: 'Eastern Europe, Balkans',
      season: ['APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP'],
      rate_per_1000_season: 0.8,
      vaccine_preventable: true,
      vaccine_reduction: 0.95,
      severity: 'MODERATE_SEVERE',
      rtd_days: 30
    },
    LYME_DISEASE: {
      region: 'Europe, North America',
      season: ['MAY', 'JUN', 'JUL', 'AUG'],
      rate_per_1000_season: 2.5,
      antibiotic_responsive: true,
      rtd_days: 14
    },
    CRIMEAN_CONGO_HEMORRHAGIC_FEVER: {
      region: 'Balkans, Turkey, Middle East',
      season: ['APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP'],
      rate_per_1000_season: 0.1,
      mortality: 0.30,
      evacuation_rate: 1.0
    }
  },
  
  // Skin infections
  SKIN_INFECTIONS: {
    rate_per_1000_day: 0.01,
    hygiene_dependent: true,
    
    conditions: {
      CELLULITIS: { percentage: 0.30, rtd_days: 7 },
      IMPETIGO: { percentage: 0.25, rtd_days: 5 },
      FUNGAL: { percentage: 0.30, rtd_days: 14 },
      SCABIES: { percentage: 0.10, rtd_days: 3 },
      MRSA: { percentage: 0.05, rtd_days: 14, evacuation_rate: 0.30 }
    }
  }
};

// ============================================================================
// ROMANIA/EASTERN EUROPE ENDEMIC DISEASES
// ============================================================================

export const ROMANIA_ENDEMIC_DISEASES = {
  region: 'Romania and Eastern Europe',
  
  diseases: {
    TICK_BORNE_ENCEPHALITIS: {
      endemic_areas: ['Transylvania', 'Carpathians', 'Moldova'],
      peak_season: ['MAY', 'JUN', 'JUL'],
      vaccine_available: true,
      military_vaccination_recommended: true,
      rate_unvaccinated: 1.2,
      rate_vaccinated: 0.05
    },
    
    LYME_DISEASE: {
      endemic_areas: ['Carpathians', 'All forested areas'],
      peak_season: ['MAY', 'JUN', 'JUL', 'AUG'],
      antibiotic_prophylaxis: true,
      rate_per_1000_season: 3.0
    },
    
    HANTAVIRUS: {
      endemic_areas: ['Rural areas', 'Agricultural regions'],
      transmission: 'Rodent contact/aerosolized droppings',
      rate_per_1000_year: 0.3,
      mortality: 0.05
    },
    
    WEST_NILE_VIRUS: {
      endemic_areas: ['Danube Delta', 'Lowland areas'],
      peak_season: ['JUL', 'AUG', 'SEP'],
      rate_per_1000_season: 0.2
    },
    
    RABIES: {
      endemic: true,
      source: 'Stray dogs, foxes, bats',
      post_exposure_prophylaxis: 'AVAILABLE',
      notes: 'Pre-deployment vaccination recommended'
    }
  },
  
  preventive_measures: [
    'Tick-borne encephalitis vaccination',
    'Tick checks after field operations',
    'DEET insect repellent',
    'Proper uniform wear in forests',
    'Avoid rodent-infested areas',
    'Water purification',
    'Rabies pre-exposure vaccination for high-risk personnel'
  ]
};

// ============================================================================
// DURATION EFFECTS
// ============================================================================

export const DNBI_DURATION_MODIFIERS = {
  DAYS_1_7: { label: 'Week 1', modifier: 0.8, notes: 'Initial period, troops fresh' },
  DAYS_8_30: { label: 'Month 1', modifier: 1.0, notes: 'Baseline operational tempo' },
  DAYS_31_60: { label: 'Month 2', modifier: 1.15, notes: 'Fatigue setting in' },
  DAYS_61_90: { label: 'Month 3', modifier: 1.3, notes: 'Significant fatigue' },
  DAYS_91_180: { label: 'Months 4-6', modifier: 1.5, notes: 'Extended deployment effects' },
  DAYS_181_270: { label: 'Months 7-9', modifier: 1.7, notes: 'High cumulative DNBI' },
  DAYS_271_365: { label: 'Months 10-12', modifier: 1.9, notes: 'Near deployment limit' },
  DAYS_365_PLUS: { label: '12+ Months', modifier: 2.2, notes: 'Exceeds recommended limits' }
};

export const getDurationModifier = (dayNumber) => {
  if (dayNumber <= 7) return DNBI_DURATION_MODIFIERS.DAYS_1_7;
  if (dayNumber <= 30) return DNBI_DURATION_MODIFIERS.DAYS_8_30;
  if (dayNumber <= 60) return DNBI_DURATION_MODIFIERS.DAYS_31_60;
  if (dayNumber <= 90) return DNBI_DURATION_MODIFIERS.DAYS_61_90;
  if (dayNumber <= 180) return DNBI_DURATION_MODIFIERS.DAYS_91_180;
  if (dayNumber <= 270) return DNBI_DURATION_MODIFIERS.DAYS_181_270;
  if (dayNumber <= 365) return DNBI_DURATION_MODIFIERS.DAYS_271_365;
  return DNBI_DURATION_MODIFIERS.DAYS_365_PLUS;
};

// ============================================================================
// DEMOGRAPHIC MODIFIERS
// ============================================================================

export const DEMOGRAPHIC_MODIFIERS = {
  // Age effects
  AGE: {
    '18-24': { injury_modifier: 1.1, disease_modifier: 0.9 },
    '25-34': { injury_modifier: 1.0, disease_modifier: 1.0 },
    '35-44': { injury_modifier: 1.15, disease_modifier: 1.1 },
    '45+': { injury_modifier: 1.3, disease_modifier: 1.2 }
  },
  
  // Sex effects (based on BCT OIF study: females 67% higher DNBI)
  SEX: {
    MALE: { modifier: 1.0 },
    FEMALE: { 
      modifier: 1.67,
      specific_conditions: {
        musculoskeletal: 1.8,  // Higher MSK injury rate
        psychiatric: 1.5,
        gynecological: 'ADDITIONAL'  // Additional category
      },
      notes: 'BCT OIF study: females IRR 1.67 vs males'
    }
  },
  
  // Fitness level
  FITNESS: {
    EXCELLENT: { modifier: 0.75 },
    GOOD: { modifier: 0.9 },
    AVERAGE: { modifier: 1.0 },
    BELOW_AVERAGE: { modifier: 1.3 },
    POOR: { modifier: 1.6 }
  }
};

// ============================================================================
// DNBI CATEGORY BREAKDOWN
// ============================================================================

// CALC-014: the DNBI_CATEGORIES entry that holds behavioural health (combat stress) casualties.
// dnbiModule reports it separately as BEHAVIORAL_HEALTH and must not count it again among the others.
export const BEHAVIORAL_HEALTH_SOURCE_CATEGORY = 'PSYCHIATRIC';

export const DNBI_CATEGORIES = {
  MUSCULOSKELETAL: {
    label: 'Musculoskeletal',
    percentage: 0.504,
    subcategories: {
      back_injury: 0.25,
      knee_injury: 0.20,
      ankle_sprain: 0.15,
      shoulder: 0.12,
      overuse: 0.18,
      other: 0.10
    },
    typical_rtd_days: 7,
    evacuation_rate: 0.15,
    notes: 'Most common DNBI category'
  },
  
  PSYCHIATRIC: {
    label: 'Psychiatric/Behavioral',
    percentage: 0.233,
    subcategories: {
      adjustment_disorder: 0.35,
      combat_stress_reaction: 0.25,
      mood_disorder: 0.20,
      anxiety: 0.12,
      ptsd: 0.08
    },
    typical_rtd_days: 14,
    // 2026-10-10: was 0.43, a misreading of Belmont et al. 2010, where musculoskeletal + psychiatric were 43% of ALL
    // evacuated DNBI casualties. For psychiatric casualties themselves the same brigade reports 88% treated in theatre
    // and returned to duty, 11% evacuated, 1% died (epidemiology of psychiatric disorders, same unit). With 0.11 the
    // category-weighted evacuation rate is 16.4%, against 15.7% evacuated in the whole brigade.
    evacuation_rate: 0.11,
    notes: 'Second most common. 88% treated in theatre and returned to duty (Belmont 2010 psychiatric paper).'
  },
  
  DISEASE: {
    label: 'Disease/Infection',
    percentage: 0.12,
    subcategories: {
      respiratory: 0.35,
      gastrointestinal: 0.30,
      skin_infection: 0.15,
      vector_borne: 0.10,
      other_infectious: 0.10
    },
    typical_rtd_days: 5,
    evacuation_rate: 0.10,
    notes: 'Varies significantly by environment'
  },
  
  VEHICLE_ACCIDENTS: {
    label: 'Vehicle Accidents',
    percentage: 0.08,
    subcategories: {
      rollover: 0.40,
      collision: 0.30,
      dismount: 0.20,
      maintenance: 0.10
    },
    typical_rtd_days: 14,
    evacuation_rate: 0.35,
    notes: 'Often serious injuries'
  },
  
  COLD_INJURY: {
    label: 'Cold Injury',
    percentage: 0.03,
    seasonal: true,
    peak_months: ['DEC', 'JAN', 'FEB'],
    subcategories: {
      frostbite: 0.45,
      trench_foot: 0.30,
      hypothermia: 0.20,
      chilblains: 0.05
    },
    typical_rtd_days: 14,
    evacuation_rate: 0.40,
    notes: 'Winter operations, trench warfare'
  },
  
  HEAT_INJURY: {
    label: 'Heat Injury',
    percentage: 0.02,
    seasonal: true,
    peak_months: ['JUN', 'JUL', 'AUG'],
    subcategories: {
      heat_exhaustion: 0.70,
      heat_stroke: 0.15,
      heat_cramps: 0.10,
      heat_syncope: 0.05
    },
    typical_rtd_days: 3,
    evacuation_rate: 0.20,
    notes: 'Summer operations, desert'
  },
  
  OTHER_INJURY: {
    label: 'Other Non-Battle Injury',
    percentage: 0.033,
    subcategories: {
      sports_pt: 0.35,
      falls: 0.25,
      equipment: 0.20,
      burns_non_combat: 0.10,
      other: 0.10
    },
    typical_rtd_days: 7,
    evacuation_rate: 0.20,
    notes: 'Miscellaneous NBI'
  }
};

// ============================================================================
// UKRAINE 2022-2024 LESSONS
// ============================================================================

/**
 * DNBI that needs holding goes to Role 2 beds (Dr. Tatar, 2026-10-10); behavioural health casualties stay forward
 * (restoration, BICEPS) and do not use Role 2B beds. Length of stay of a DNBI admission: mean 2.6 days, median 2
 * (UK Role 3 hospital at Shaibah, Iraq; 91% of its 3,598 admissions were DNBI). A UK source, not US.
 */
export const DNBI_HOLDING = {
  mean_los_days: 2.6,
  behavioral_health_uses_role2_beds: false,
  source: 'UK Role 3 Shaibah, Iraq: 3,598 admissions, 91% DNBI, length of stay mean 2.6 d (SD 1.7), median 2 d',
  status: 'PLACEHOLDER'
};

export const UKRAINE_DNBI_LESSONS = {
  summary: 'Largest conventional war DNBI data since Korean War',
  
  key_findings: {
    trench_warfare: {
      description: 'Static trench positions dramatically increase DNBI',
      modifier: 1.4,
      primary_issues: ['trench_foot', 'respiratory', 'psychiatric']
    },
    
    winter_operations: {
      description: '2022-2023 winter showed high cold injury rates',
      trench_foot_rate: 0.08,  // 8% of winter casualties
      cold_injury_modifier: 1.6,
      notes: 'Many units lacked proper cold weather equipment'
    },
    
    drone_stress: {
      description: 'Constant FPV drone threat creates persistent psychological stress',
      psych_modifier: 1.3,
      notes: 'Soldiers unable to rest even in "safe" positions'
    },
    
    hygiene_challenges: {
      description: 'Extended field operations without hygiene facilities',
      disease_modifier: 1.5,
      skin_infection_rate: 0.12
    },
    
    rotation_critical: {
      optimal_frontline_days: 45,
      maximum_frontline_days: 90,
      dnbi_increase_past_90_days: 1.8
    }
  },
  
  recommendations: [
    'Rotate units every 45-60 days maximum',
    'Ensure cold weather equipment in winter',
    'Establish warming/drying facilities',
    'Provide mental health support forward',
    'Maintain hygiene standards despite conditions',
    'Stock foot care supplies (powder, socks, boots)'
  ]
};

// ============================================================================
// THEATER-SPECIFIC HISTORICAL DATA
// ============================================================================

export const THEATER_DNBI_DATA = {
  AFGHANISTAN_OEF: {
    label: 'Afghanistan (OEF)',
    years: '2001-2013',
    dnbi_medevac_rate: 56.7,
    battle_injury_rate: 12.0,
    dnbi_to_bi_ratio: 4.7,
    leading_causes: {
      nonbattle_injury: 0.31,
      battle_injury: 0.20,
      behavioral_health: 0.12
    },
    notes: 'Mountainous terrain increased falls and altitude issues'
  },
  
  IRAQ_OIF: {
    label: 'Iraq (OIF/OND)',
    years: '2003-2011',
    dnbi_medevac_rate: 40.2,
    battle_injury_rate: 7.7,
    dnbi_to_bi_ratio: 5.2,
    notes: 'Heat casualties significant. Higher vehicle accident rate.'
  },
  
  UKRAINE_2022_2024: {
    label: 'Ukraine',
    years: '2022-2024',
    estimated_dnbi_rate: 65.0,  // Higher than OEF due to trench conditions
    dnbi_to_bi_ratio: 3.5,      // Lower ratio due to high combat intensity
    unique_factors: ['trench_foot', 'drone_stress', 'winter_cold'],
    notes: 'Highest intensity conventional warfare since Korea'
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate daily DNBI for a unit
 */
export const calculateDailyDNBI = (strength, operationalState, environment = {}, dayNumber = 1) => {
  const stateData = DNBI_RATES_BY_OPERATIONAL_STATE[operationalState] || 
                    DNBI_RATES_BY_OPERATIONAL_STATE.SUPPORT_ACTIVE;
  
  let baseRate = stateData.combined_rate;
  
  // Get environmental modifiers
  const climateData = DNBI_ENVIRONMENTAL_MODIFIERS.CLIMATE[environment.climate || 'TEMPERATE'];
  const terrainData = DNBI_ENVIRONMENTAL_MODIFIERS.TERRAIN[environment.terrain || 'OPEN'];
  const sanitationData = DNBI_ENVIRONMENTAL_MODIFIERS.WATER_SANITATION[environment.sanitation || 'GOOD'];
  
  // Apply monthly modifier if available
  let monthlyMod = 1.0;
  if (environment.month && climateData.monthly_modifiers) {
    monthlyMod = climateData.monthly_modifiers[environment.month] || 1.0;
  }
  
  const envModifier = ((climateData.modifier || 1.0) + (terrainData.modifier || 1.0) + (sanitationData.modifier || 1.0)) / 3;
  
  // Apply duration modifier
  const durationData = getDurationModifier(dayNumber);
  
  // Calculate adjusted rate
  const adjustedRate = baseRate * envModifier * monthlyMod * durationData.modifier;
  
  // Calculate casualties
  const totalDNBI = (strength / 1000) * adjustedRate;
  
  // Break down by category
  const breakdown = {};
  Object.keys(DNBI_CATEGORIES).forEach(cat => {
    breakdown[cat] = Math.round(totalDNBI * DNBI_CATEGORIES[cat].percentage * 10) / 10;
  });
  
  const evacuationsNeeded = Math.round(totalDNBI * stateData.evacuation_rate * 10) / 10;
  const rtd = Math.round(totalDNBI * stateData.rtd_rate * 10) / 10;
  
  return {
    total: Math.round(totalDNBI * 10) / 10,
    disease: Math.round((strength / 1000) * stateData.disease_rate * envModifier * monthlyMod * durationData.modifier * 10) / 10,
    non_battle_injury: Math.round((strength / 1000) * stateData.non_battle_injury_rate * envModifier * monthlyMod * durationData.modifier * 10) / 10,
    breakdown,
    evacuations_needed: evacuationsNeeded,
    return_to_duty: rtd,
    rate_per_1000_day: Math.round(adjustedRate * 1000) / 1000,
    modifiers_applied: {
      base_rate: baseRate,
      environment: Math.round(envModifier * 100) / 100,
      monthly: monthlyMod,
      duration: durationData.modifier,
      final_rate: Math.round(adjustedRate * 1000) / 1000
    }
  };
};

/**
 * Calculate cumulative DNBI over operation duration
 */
export const calculateCumulativeDNBI = (strength, operationalState, environment, durationDays) => {
  let cumulative = {
    total: 0,
    disease: 0,
    non_battle_injury: 0,
    evacuations: 0,
    by_day: []
  };
  
  for (let day = 1; day <= durationDays; day++) {
    const daily = calculateDailyDNBI(strength, operationalState, environment, day);
    
    cumulative.total += daily.total;
    cumulative.disease += daily.disease;
    cumulative.non_battle_injury += daily.non_battle_injury;
    cumulative.evacuations += daily.evacuations_needed;
    
    if (day <= 30 || day % 7 === 0) {  // Store first 30 days and weekly after
      cumulative.by_day.push({
        day,
        daily_total: daily.total,
        cumulative_total: Math.round(cumulative.total),
        rate: daily.rate_per_1000_day
      });
    }
  }
  
  cumulative.total = Math.round(cumulative.total);
  cumulative.disease = Math.round(cumulative.disease);
  cumulative.non_battle_injury = Math.round(cumulative.non_battle_injury);
  cumulative.evacuations = Math.round(cumulative.evacuations);
  
  return cumulative;
};

/**
 * Calculate cold injury risk
 */
export const calculateColdInjuryRisk = (temperature_c, wind_kmh, wet_conditions, exposure_hours) => {
  // Get base rate from temperature
  let baseRate = 0.01;
  const thresholds = COLD_INJURY_MODEL.temperature_thresholds;
  
  if (temperature_c <= thresholds.EXTREME.temp_c) baseRate = thresholds.EXTREME.rate_per_1000_day;
  else if (temperature_c <= thresholds.DANGER.temp_c) baseRate = thresholds.DANGER.rate_per_1000_day;
  else if (temperature_c <= thresholds.WARNING.temp_c) baseRate = thresholds.WARNING.rate_per_1000_day;
  else if (temperature_c <= thresholds.CAUTION.temp_c) baseRate = thresholds.CAUTION.rate_per_1000_day;
  else baseRate = thresholds.SAFE.rate_per_1000_day;
  
  // Wind chill multiplier
  let windMult = 1.0;
  if (wind_kmh > 60) windMult = COLD_INJURY_MODEL.wind_chill_multiplier.severe;
  else if (wind_kmh > 40) windMult = COLD_INJURY_MODEL.wind_chill_multiplier.strong;
  else if (wind_kmh > 20) windMult = COLD_INJURY_MODEL.wind_chill_multiplier.moderate;
  else if (wind_kmh > 10) windMult = COLD_INJURY_MODEL.wind_chill_multiplier.light;
  
  // Wet conditions
  const wetMult = wet_conditions ? COLD_INJURY_MODEL.wet_cold_multiplier : 1.0;
  
  // Exposure time adjustment
  const exposureMult = Math.min(exposure_hours / 8, 2.0);
  
  const finalRate = baseRate * windMult * wetMult * exposureMult;
  
  return {
    rate_per_1000_day: Math.round(finalRate * 100) / 100,
    risk_level: finalRate > 2 ? 'HIGH' : finalRate > 0.5 ? 'MODERATE' : 'LOW',
    factors: {
      temperature: temperature_c,
      wind_multiplier: windMult,
      wet_multiplier: wetMult,
      exposure_multiplier: exposureMult
    },
    prevention_recommendations: finalRate > 0.5 ? COLD_INJURY_MODEL.prevention_measures : null
  };
};

/**
 * Calculate heat injury risk
 */
export const calculateHeatInjuryRisk = (heat_index_c, work_intensity, equipment_level, acclimatization_days) => {
  let baseRate = 0.05;
  const thresholds = HEAT_INJURY_MODEL.heat_index_thresholds;
  
  if (heat_index_c >= thresholds.EXTREME.temp_c) baseRate = thresholds.EXTREME.rate_per_1000_day;
  else if (heat_index_c >= thresholds.DANGER.temp_c) baseRate = thresholds.DANGER.rate_per_1000_day;
  else if (heat_index_c >= thresholds.WARNING.temp_c) baseRate = thresholds.WARNING.rate_per_1000_day;
  else if (heat_index_c >= thresholds.CAUTION.temp_c) baseRate = thresholds.CAUTION.rate_per_1000_day;
  
  const workMult = HEAT_INJURY_MODEL.work_intensity[work_intensity] || 1.0;
  const equipMult = HEAT_INJURY_MODEL.equipment_burden[equipment_level] || 1.0;
  
  // Acclimatization reduction
  const accl = HEAT_INJURY_MODEL.acclimatization;
  const acclReduction = Math.min(acclimatization_days * accl.reduction_per_day, accl.max_reduction);
  const acclMult = 1 - acclReduction;
  
  const finalRate = baseRate * workMult * equipMult * acclMult;
  
  return {
    rate_per_1000_day: Math.round(finalRate * 100) / 100,
    risk_level: finalRate > 2 ? 'HIGH' : finalRate > 0.5 ? 'MODERATE' : 'LOW',
    factors: {
      heat_index: heat_index_c,
      work_multiplier: workMult,
      equipment_multiplier: equipMult,
      acclimatization_reduction: Math.round(acclReduction * 100) + '%'
    }
  };
};

/**
 * Get DNBI rate for a specific unit type
 */
export const getDNBIRateForUnitType = (unitType, operationalState) => {
  const stateRate = DNBI_RATES_BY_OPERATIONAL_STATE[operationalState]?.combined_rate || 0.13;
  
  const unitTypeModifiers = {
    INFANTRY: 1.2,
    MECHANIZED: 1.0,
    ARMOR: 0.9,
    ARTILLERY: 0.85,
    ENGINEERS: 1.1,
    SIGNAL: 0.8,
    LOGISTICS: 0.9,
    MEDICAL: 0.85,
    AVIATION: 0.75,
    HQ: 0.7
  };
  
  const modifier = unitTypeModifiers[unitType] || 1.0;
  return {
    rate: stateRate * modifier,
    unit_type: unitType,
    modifier: modifier
  };
};

/**
 * Get seasonal DNBI adjustment for Carpathian operations
 */
export const getCarpathianSeasonalModifier = (month, elevation_m = 1000) => {
  const monthData = CARPATHIAN_CLIMATE.monthly_conditions[month];
  if (!monthData) return { modifier: 1.0 };
  
  // Elevation adjustment
  let elevMod = 1.0;
  const elevRanges = CARPATHIAN_CLIMATE.elevation_ranges;
  if (elevation_m >= 2000) elevMod = elevRanges.ALPINE.modifier;
  else if (elevation_m >= 1500) elevMod = elevRanges.HIGH.modifier;
  else if (elevation_m >= 1000) elevMod = elevRanges.MODERATE.modifier;
  else elevMod = elevRanges.LOW.modifier;
  
  const combinedModifier = monthData.modifier * elevMod;
  
  return {
    modifier: Math.round(combinedModifier * 100) / 100,
    month: month,
    elevation_m: elevation_m,
    temp_avg_c: monthData.temp_avg_c,
    cold_injury_risk: monthData.cold_injury_risk,
    snow_depth_cm: monthData.snow_depth_cm
  };
};

/**
 * Calculate mental health DNBI for unit
 */
export const calculateMentalHealthDNBI = (strength, operationalState, deploymentDays, riskFactors = []) => {
  const baseRate = MENTAL_HEALTH_DNBI.base_rates[operationalState] || 0.025;
  
  // Apply risk factors
  let riskMultiplier = 1.0;
  riskFactors.forEach(factor => {
    if (MENTAL_HEALTH_DNBI.risk_factors[factor]) {
      riskMultiplier *= MENTAL_HEALTH_DNBI.risk_factors[factor];
    }
  });
  
  // Duration effect
  const durationMod = getDurationModifier(deploymentDays);
  
  const adjustedRate = baseRate * riskMultiplier * durationMod.modifier;
  const dailyPsychCasualties = (strength / 1000) * adjustedRate;
  
  // Breakdown by condition
  const breakdown = {};
  Object.entries(MENTAL_HEALTH_DNBI.conditions).forEach(([key, data]) => {
    breakdown[key] = Math.round(dailyPsychCasualties * data.percentage * 10) / 10;
  });
  
  return {
    daily_total: Math.round(dailyPsychCasualties * 10) / 10,
    rate_per_1000_day: Math.round(adjustedRate * 1000) / 1000,
    breakdown,
    evacuation_estimate: Math.round(dailyPsychCasualties * 0.43 * 10) / 10,  // 43% evac rate
    risk_multiplier: Math.round(riskMultiplier * 100) / 100
  };
};

/**
 * Validate operational state
 */
export const isValidOperationalState = (state) => {
  return Object.keys(DNBI_RATES_BY_OPERATIONAL_STATE).includes(state);
};

/**
 * Get all operational states for dropdowns
 */
export const getOperationalStateOptions = () => {
  return Object.entries(DNBI_RATES_BY_OPERATIONAL_STATE).map(([key, data]) => ({
    value: key,
    label: data.label,
    rate: data.combined_rate
  }));
};

// ============================================================================
// EXPORTS
// ============================================================================

// Named default export (fixes lint warning)
const dnbiRatesConstants = {
  // Core rates
  DNBI_RATES_BY_OPERATIONAL_STATE,
  DNBI_RATES_BY_ECHELON,
  DNBI_ENVIRONMENTAL_MODIFIERS,
  DNBI_DURATION_MODIFIERS,
  DNBI_CATEGORIES,
  
  // Climate specifics
  CARPATHIAN_CLIMATE,
  COLD_INJURY_MODEL,
  HEAT_INJURY_MODEL,
  
  // Health specifics
  MENTAL_HEALTH_DNBI,
  INFECTIOUS_DISEASE_RATES,
  ROMANIA_ENDEMIC_DISEASES,
  DEMOGRAPHIC_MODIFIERS,
  
  // Historical data
  THEATER_DNBI_DATA,
  UKRAINE_DNBI_LESSONS,
  
  // Helper functions
  getDurationModifier,
  calculateDailyDNBI,
  calculateCumulativeDNBI,
  calculateColdInjuryRisk,
  calculateHeatInjuryRisk,
  getDNBIRateForUnitType,
  getCarpathianSeasonalModifier,
  calculateMentalHealthDNBI,
  isValidOperationalState,
  getOperationalStateOptions
};

export default dnbiRatesConstants;


/**
 * Return to duty of non-battle illness and injury cases (2026-10-11, Dr. Tatar: they leave the fight while treated and
 * come back after treatment). Days from the day of the case to the return of those who return (about 84 percent).
 * Category times in this file: URTI 3 days, heat exhaustion 24 hours, heat cramps 4 hours, influenza 7 days, combat stress
 * within 72 hours; cases held at Role 2B stay 2.6 days on average (DNBI_HOLDING). EDUCATED GUESS: 3 days for all.
 */
export const DNBI_RETURN_TO_DUTY = {
  delay_days: 3,
  status: 'EDUCATED_GUESS',
  basis: 'Category return times 4 hours to 7 days (URTI 3, influenza 7, combat stress 72 h); held cases 2.6 days (DNBI_HOLDING).'
};


/**
 * Non-battle illness and injury on a march (2026-10-13, Tab 2 plan step 6). It was a fixed 30 per 1,000 per day (25 for transit),
 * written as "3 percent vs 2 percent normal", about six times the 4.7 per 1,000 of a combat-engaged brigade state: 150 cases a
 * day for a brigade. The march rate is now the normal DNBI rate of the operational state times the ratio of the old comment
 * (1.5; transit 1.25). EDUCATED GUESS until the DNBI base rate is decided (open item) and a march source is found.
 */
export const MARCH_DNBI_FACTOR = {
  march: 1.5,
  transit: 1.25,
  status: 'EDUCATED_GUESS',
  basis: 'Ratio of the former code comment (3 percent on a march against 2 percent normal; transit 2.5 percent). Cramped vehicles, fatigue, hygiene, disrupted rest.'
};

/** The former fixed march rates, kept for reference (per 1,000 per day). Not used. */
export const LEGACY_MARCH_DNBI_PER_1000 = { march: 30, transit: 25 };
